import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppText from "../components/app-text";
import { OffreService } from "../service/offre.service";
import SearchForm from "../search-form";

export default function Offres() {
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lng, setLng] = useState(null);
  const [expandedOffres, setExpandedOffres] = useState(new Set()); // Track expanded offers

  useEffect(() => {
    // Get current language
    setLng(localStorage.getItem("lng"));
    
    OffreService
      .getActive()
      .then((res) => {
        setOffres(res);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading offers:", error);
        setLoading(false);
      });
  }, []);

  // Helper function to get title in current language
  const getOffreTitle = (offre) => {
    return offre[`title_${lng}`] || offre.title_fr || `Offre ${offre.id}`;
  };

  // Helper function to get content in current language
  const getOffreContent = (offre) => {
    return offre[`content_${lng}`] || offre.content_fr || "";
  };

  // Helper function to get rich content in current language
  const getOffreRichContent = (offre) => {
    return offre[`contenu_${lng}`] || offre.contenu_fr || "";
  };

  // Toggle rich content visibility
  const toggleRichContent = (offreId) => {
    const newExpanded = new Set(expandedOffres);
    if (newExpanded.has(offreId)) {
      newExpanded.delete(offreId);
    } else {
      newExpanded.add(offreId);
    }
    setExpandedOffres(newExpanded);
  };

  // Check if an offer has rich content
  const hasRichContent = (offre) => {
    return getOffreRichContent(offre).trim().length > 0;
  };

  if (loading || lng === null) {
    return (
      <div className="loading-container">
        <p><AppText cle="chargement" /></p>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <div
        style={{
          height: "100vh",
          background: `url('assets/img/slide/slide-1.jpg')`,
          backgroundSize: "cover",
        }}
      >
        <div
          style={{
            height: "100%",
            background: `rgba(0,0,0,.6)`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <h1
            className="container text-center"
            style={{ color: "var(--primary-color)" }}
          >
            <AppText cle="offres_titre" />
          </h1>
          <h2 className="container text-center" style={{ color: "white" }}>
            <AppText cle="offres_sous_titre" />
          </h2>
          <SearchForm />
        </div>
      </div>

      {/* Offers Content */}
      <div className="container py-5">
        {offres.length === 0 ? (
          <div className="text-center">
            <h3><AppText cle="aucune_offre" /></h3>
            <p><AppText cle="aucune_offre_description" /></p>
          </div>
        ) : (
          <div className="row">
            {offres.map((offre, index) => (
              <div key={offre.id} className="col-12 mb-5">
                <div className="offre-card">
                  <div className="row">                    {/* Image Column */}
                    <div className="col-md-4">
                      <div className="offre-image-container">
                        {offre.image ? (
                          <img
                            src={offre.image}
                            alt={getOffreTitle(offre)}
                            className="offre-image"
                            style={{
                              width: "100%",
                              height: "300px",
                              objectFit: "cover",
                              borderRadius: "8px"
                            }}
                          />
                        ) : (
                          <div
                            className="offre-image-placeholder"
                            style={{
                              width: "100%",
                              height: "300px",
                              backgroundColor: "#f0f0f0",
                              borderRadius: "8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center"
                            }}
                          >
                            <span>Image offre</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Content Column */}
                    <div className="col-md-8">
                      <div className="offre-content p-4">
                        <h2 className="offre-title mb-4">{getOffreTitle(offre)}</h2>
                        
                        <div className="offre-description">
                          <p style={{ fontSize: "1.1rem", lineHeight: "1.6" }}>
                            {getOffreContent(offre)}
                          </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="offre-actions mt-4">
                          {(offre.type === 'accommodation' || offre.is_accommodation) && (
                            <button 
                              className="btn btn-primary btn-lg me-3"
                              style={{
                                backgroundColor: "var(--primary-color)",
                                borderColor: "var(--primary-color)",
                                padding: "12px 30px"
                              }}
                              onClick={() =>
                                window.open(
                                  "https://direct-book.com/properties/HotelRoyalASBUTunisDIRECT",
                                  "_blank"
                                )
                              }
                            >
                              <i className="bi bi-calendar-check me-2"></i>
                              <AppText cle="reserver" />
                            </button>                          )}
                          
                          {/* Plus d'info button - only show if there's rich content */}
                          {hasRichContent(offre) && (
                            <button 
                              className="btn btn-outline-secondary btn-lg"
                              onClick={() => toggleRichContent(offre.id)}
                            >
                              <i className={`bi ${expandedOffres.has(offre.id) ? 'bi-eye-slash' : 'bi-info-circle'} me-2`}></i>
                              <AppText cle={expandedOffres.has(offre.id) ? "moins_info" : "plus_info"} />
                            </button>
                          )}
                        </div>

                        {/* Rich Content Section - Show when expanded */}
                        {expandedOffres.has(offre.id) && hasRichContent(offre) && (
                          <div className="rich-content-section mt-4 p-4" style={{
                            backgroundColor: "#f8f9fa",
                            borderRadius: "8px",
                            border: "1px solid #e9ecef"
                          }}>
                            <h5 className="mb-3" style={{ color: "var(--primary-color)" }}>
                              <i className="bi bi-info-circle me-2"></i>
                              {/* <AppText cle="details_offre" /> */}
                            </h5>
                            <div 
                              className="rich-content"
                              dangerouslySetInnerHTML={{ __html: getOffreRichContent(offre) }}
                              style={{
                                fontSize: "1rem",
                                lineHeight: "1.6",
                                color: "#495057"
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Separator */}
                {index < offres.length - 1 && (
                  <hr className="my-5" style={{ borderColor: "var(--primary-color)" }} />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Back to Homepage Link */}
        <div className="text-center mt-5">
          <Link 
            to="/" 
            className="btn btn-outline-primary"
          >
            <i className="bi bi-arrow-left me-2"></i>
            <AppText cle="retour_accueil" />
          </Link>
        </div>
      </div>
    </div>
  );
} 